/**
 * The logotype of discordant.org: "discordant" in Fraunces, ".org" in Recursive Mono, set as
 * DOM text and kept alive by damped springs.
 *
 * Every glyph sits in a fixed-width box measured at rest, so nothing it does — weight flux, a
 * face change, a substituted letter, a dither block — can move its neighbours or the word.
 * Motion is a tremor of a fixed thing: offsets stay within a few percent of an em and every
 * channel springs home.
 *
 *   - rest state drifts slowly (seeded simplex noise, out of phase per glyph): a heat shimmer
 *   - the pointer brushes the letters: they lean away and take a kick from its velocity
 *   - a strike on the bus schedules a kick for the moment the wave front reaches each glyph
 *   - rare discord events: cut-in (the other face), homoglyph slip (a look-alike, set in the other
 *     face), bit-rot; the rose on a chord
 *
 * Nothing here depends on audio. Under ?debug=logo the module emits synthetic strikes and chords
 * and listens for keyboard shortcuts (see DEBUG_HELP).
 */
import '../styles/logo.css';

import type { Bus } from '../bus';
import type { BusEvents, PointerInfo, Quality } from '../types';
import { CONFIG, DEV } from '../config';
import { TAU, clamp, clamp01, hashString, mulberry32 } from '../util/math';
import { Simplex } from '../util/noise';

export interface LogoOptions {
  container: HTMLElement;
  bus: Bus;
  quality: Quality;
}

export interface LogoHandle {
  /** per frame; pointer is the primary pointer in CSS px, or null */
  update(dt: number, now: number, pointer: PointerInfo | null): void;
  /** viewport changed: re-measure glyph rectangles */
  relayout(): void;
  dispose(): void;
  /** DEV only: force an event on a glyph by index (word glyphs first, then ".org"), or the rose */
  dev?: {
    event(kind: 'cut' | 'slip' | 'rot', index: number): boolean;
    rose(): void;
    slipReady(): { word: boolean; tld: boolean };
  };
}

// ---------------------------------------------------------------------------
// Knobs
// ---------------------------------------------------------------------------
const TUNE = {
  /** rest axes */
  rest: { wght: 420, soft: 55, casl: 0.12, wghtDrift: 25, softDrift: 20, caslDrift: 0.12 },
  /** drift periods (s) per glyph, uniform in [min, max]; phases are seeded */
  drift: { minPeriod: 6, maxPeriod: 14 },
  /** springs: natural frequency (Hz) and damping ratio per channel */
  spring: {
    wght: { hz: 2.2, zeta: 0.28 },
    soft: { hz: 2.0, zeta: 0.30 },
    pos: { hz: 3.6, zeta: 0.42 },
    rot: { hz: 3.0, zeta: 0.36 },
    scale: { hz: 4.0, zeta: 0.60 },
  },
  /** hard limits (em, deg, axis units) */
  limit: { offsetEm: 0.06, rotDeg: 2, wght: [300, 720] as const, soft: [0, 100] as const, casl: [0, 1] as const, scale: [0.9, 1.25] as const },
  /** pointer coupling */
  pointer: { radiusPx: 120, leanEm: 0.035, leanYEm: 0.018, leanDeg: 1.7, gain: 4.6, rotGain: 30, maxSpeed: 2400, appleRadiusPx: 40, appleScale: 1.15 },
  /**
   * strike coupling. Impact velocity is compressed (v^gamma, lifted by a floor) so wind strikes
   * (0.1–0.3) still ring the word while a hand's sweep (0.6–0.75) reads as the loudest thing; then a
   * Lorentzian falloff over distance (px). Kicks are spring velocities per unit magnitude.
   */
  strike: { gamma: 0.6, floor: 0.06, falloffPx: 720, wghtKick: 3600, softKick: 1000, yKickEm: 1.5, rotKickDeg: 18, heatGain: 0.55, heatTau: 3.5 },
  /** discord events */
  events: {
    minGap: 6, maxGap: 20, rareFactor: 1.8, heatAccel: 3, maxActive: 2, cooldown: 0.9,
    weights: { cut: 0.42, slip: 0.33, rot: 0.25 },
    cutMs: [400, 900] as const, slipMs: [250, 600] as const, rotMs: 300,
    /** chance that a strong wave arrival flips the glyph it just reached; the floor is on the compressed magnitude scale (0.6 ≈ velocity 0.43 at the word) */
    waveTrigger: 0.14, waveMinMag: 0.6,
  },
  /** the other face inside this face's box: Recursive at --cut-scale needs +wght to match Fraunces' colour */
  otherFace: { wghtComp: 80 },
  /** homoglyph slip: CASL of a word glyph slipped into Recursive (0 linear, 1 casual; the cut-in uses 1) */
  slip: { casl: 0 },
  rose: {
    drawSec: 1.2, holdUntil: 3.8, totalSec: 5.0, sizeXh: 1.42, riseEm: 0.02,
    /** stroke width in viewBox units (the box is 2 units): 0.09 ≈ 1/22 of the box */
    strokeUnits: 0.09,
    /** one-stroke geometry: a tight bud spiral, then petal arcs with a notch between them (see rosePath) */
    shape: { budTurns: 1.75, budR0: 0.04, budR1: 0.34, petalTurns: 2, petalsPerTurn: 1.5, dip: 0.4, cusp: 0.25, grow: 0.5, radius: 0.94 },
  },
  /** substep ceiling for the integrator (s) */
  maxStep: 1 / 120,
  /** the word may use at most this fraction of the viewport width before it is scaled down */
  fitFraction: 0.92,
} as const;

/**
 * Look-alikes for the homoglyph slip, Latin-ext only. Recursive 1.085 carries no Cyrillic at all
 * (fontsource's cyrillic-ext file is a near-empty stub), so the "intruder from the other face" is
 * made explicit instead: a slipped glyph is set in the OTHER face (see startEvent and .slip in
 * logo.css). Both faces cover all of these; the Latin-ext subsets load lazily (see warmSlipFace).
 */
const SLIPS: Record<string, string[]> = {
  d: ['đ'], i: ['ı'], s: ['ș'], c: ['ç'], o: ['ơ'], r: ['ŗ'], a: ['ą'], n: ['ņ'], t: ['ț'], g: ['ģ'],
};

const DEBUG_HELP = [
  '%clogo debug%c — synthetic strikes every 1.5–4 s, a chord every 20 s.',
  'keys: 1 cut-in · 2 homoglyph slip · 3 bit-rot · 4 / r rose · 5 strike at the pointer · h this help',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface Chan { x: number; v: number }
type Row = 'word' | 'tld';
type EvKind = 'cut' | 'slip' | 'rot';

interface EventState {
  kind: EvKind;
  t0: number;
  dur: number;
  /** cut-in: frames elapsed in the return flicker (-1 = not yet returning) */
  flick: number;
  orig: string;
}

interface Glyph {
  el: HTMLSpanElement;
  text: HTMLSpanElement;
  rotEl: HTMLSpanElement;
  ch: string;
  row: Row;
  /** cached layout (CSS px, viewport space) */
  cx: number; cy: number; w: number; h: number; em: number;
  /** springs */
  wght: Chan; soft: Chan; x: Chan; y: Chan; rot: Chan; scale: Chan;
  /** drift */
  period: number; phase: number; seedOff: number;
  /** scheduled wave arrivals */
  kicks: { at: number; mag: number }[];
  ev: EventState | null;
  /** last written values, so the DOM is only touched on a real change */
  lastFvs: string; lx: number; ly: number; lr: number; ls: number;
  apple: boolean;
}

interface RoseState { t0: number; length: number }

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
function chan(x: number): Chan { return { x, v: 0 }; }

/** semi-implicit Euler spring toward target; k = ω², c = 2ζω */
function springTo(c: Chan, target: number, k: number, damp: number, dt: number): void {
  c.v += (-k * (c.x - target) - damp * c.v) * dt;
  c.x += c.v * dt;
}

function coeffs(hz: number, zeta: number): [number, number] {
  const w = TAU * hz;
  return [w * w, 2 * zeta * w];
}

const K = {
  wght: coeffs(TUNE.spring.wght.hz, TUNE.spring.wght.zeta),
  soft: coeffs(TUNE.spring.soft.hz, TUNE.spring.soft.zeta),
  pos: coeffs(TUNE.spring.pos.hz, TUNE.spring.pos.zeta),
  rot: coeffs(TUNE.spring.rot.hz, TUNE.spring.rot.zeta),
  scale: coeffs(TUNE.spring.scale.hz, TUNE.spring.scale.zeta),
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - clamp01(t), 3);
const easeInCubic = (t: number) => Math.pow(clamp01(t), 3);

/**
 * One continuous stroke in unit space, centred on the origin, radius ≤ 1: a tight bud spiral, then
 * petal arcs that each run at a nearly constant radius and pinch inward to a notch before the next,
 * larger, petal begins — the notch is what the eye reads as one petal tucking under the next.
 * Half-integer petals per turn put each turn's petals between the last turn's, so they nest.
 */
function rosePath(): { d: string; length: number } {
  const S = TUNE.rose.shape;
  const n = 900;
  const petals = S.petalTurns * S.petalsPerTurn;
  const budEnd = S.budTurns / (S.budTurns + S.petalTurns);
  let d = '';
  let length = 0;
  let px = 0;
  let py = 0;
  for (let k = 0; k <= n; k++) {
    const t = k / n;
    const th = t * (S.budTurns + S.petalTurns) * TAU;
    let r: number;
    if (t < budEnd) {
      r = S.budR0 + (S.budR1 - S.budR0) * (t / budEnd);
    } else {
      const s = Math.min(petals - 1e-6, ((t - budEnd) / (1 - budEnd)) * petals);
      const j = Math.floor(s);
      const u = s - j;
      const ring = S.budR1 + (1 - S.budR1) * ((j + 1 - S.grow + S.grow * u) / petals);
      r = ring * (1 - S.dip * (1 - Math.pow(Math.sin(u * Math.PI), S.cusp)));
    }
    r *= S.radius;
    const x = r * Math.cos(th);
    const y = r * Math.sin(th);
    if (k === 0) d = `M${x.toFixed(3)} ${y.toFixed(3)}`;
    else { d += `L${x.toFixed(3)} ${y.toFixed(3)}`; length += Math.hypot(x - px, y - py); }
    px = x; py = y;
  }
  return { d, length };
}

// ---------------------------------------------------------------------------
// createLogo
// ---------------------------------------------------------------------------
export function createLogo(opts: LogoOptions): LogoHandle {
  const { bus, quality } = opts;
  const reduced = quality.reducedMotion;
  const rare = quality.tier === 'lite' || quality.touch;
  const debug = new URLSearchParams(location.search).get('debug') === 'logo';
  const rnd = mulberry32(CONFIG.seed ^ hashString('logo'));
  const noise = new Simplex(CONFIG.seed ^ hashString('logo-drift'));
  const motion = reduced ? 0.5 : 1;

  // --- DOM ---------------------------------------------------------------
  const root = document.createElement('h1');
  root.className = 'logo';
  root.setAttribute('aria-label', `${CONFIG.logo.text}${CONFIG.logo.tld}`);

  const glyphs: Glyph[] = [];
  const probes: Record<Row, HTMLSpanElement> = { word: document.createElement('span'), tld: document.createElement('span') };

  function buildRow(row: Row, cls: string, str: string): HTMLSpanElement {
    const wrap = document.createElement('span');
    wrap.className = cls;
    probes[row].className = 'g-probe';
    wrap.appendChild(probes[row]);
    for (const ch of str) {
      const el = document.createElement('span');
      el.className = 'glyph';
      el.setAttribute('aria-hidden', 'true');
      const text = document.createElement('span');
      text.className = 'g-text';
      text.textContent = ch;
      const rotEl = document.createElement('span');
      rotEl.className = 'g-rot';
      el.append(text, rotEl);
      wrap.appendChild(el);
      glyphs.push({
        el, text, rotEl, ch, row,
        cx: 0, cy: 0, w: 0, h: 0, em: 16,
        wght: chan(TUNE.rest.wght), soft: chan(row === 'word' ? TUNE.rest.soft : TUNE.rest.casl * 100),
        x: chan(0), y: chan(0), rot: chan(0), scale: chan(1),
        period: TUNE.drift.minPeriod + rnd() * (TUNE.drift.maxPeriod - TUNE.drift.minPeriod),
        phase: rnd() * 100,
        seedOff: glyphs.length * 0.731,
        kicks: [],
        ev: null,
        lastFvs: '', lx: 0, ly: 0, lr: 0, ls: 1,
        apple: false,
      });
    }
    return wrap;
  }

  const wordEl = buildRow('word', 'logo-word', CONFIG.logo.text);
  const tldEl = buildRow('tld', 'logo-tld', CONFIG.logo.tld);
  root.append(wordEl, tldEl);
  opts.container.replaceChildren(root);

  const wordGlyphs = glyphs.filter((g) => g.row === 'word');
  const tldGlyphs = glyphs.filter((g) => g.row === 'tld');
  const dot = tldGlyphs.find((g) => g.ch === '.') ?? null;
  const roseGlyph = tldGlyphs.find((g) => g.ch === 'o') ?? null;

  // --- the rose ------------------------------------------------------------
  const SVG_NS = 'http://www.w3.org/2000/svg';
  const roseSvg = document.createElementNS(SVG_NS, 'svg');
  roseSvg.setAttribute('class', 'g-rose');
  roseSvg.setAttribute('viewBox', '-1 -1 2 2');
  roseSvg.setAttribute('aria-hidden', 'true');
  const rosePathEl = document.createElementNS(SVG_NS, 'path');
  const roseGeom = rosePath();
  rosePathEl.setAttribute('d', roseGeom.d);
  rosePathEl.setAttribute('stroke-width', String(TUNE.rose.strokeUnits));
  rosePathEl.style.strokeDasharray = String(roseGeom.length);
  rosePathEl.style.strokeDashoffset = String(roseGeom.length);
  roseSvg.appendChild(rosePathEl);
  if (roseGlyph) roseGlyph.el.appendChild(roseSvg);
  let rose: RoseState | null = null;

  // --- state -----------------------------------------------------------------
  let now = 0;
  let heat = 0;
  let ready = false;
  let disposed = false;
  let nextEventAt = 0;
  let lastEventAt = -1e9;
  let rootEm = 16;
  let pointerPrev: { x: number; y: number } | null = null;

  // --- layout ----------------------------------------------------------------
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');

  function inkOf(g: Glyph): { asc: number; desc: number; left: number; right: number; adv: number } {
    if (!ctx) return { asc: g.em * 0.5, desc: 0, left: 0, right: g.w, adv: g.w };
    ctx.font = `${TUNE.rest.wght} ${g.em}px ${g.row === 'word' ? "'Fraunces Variable'" : "'Recursive Variable'"}`;
    const m = ctx.measureText(g.ch);
    return { asc: m.actualBoundingBoxAscent, desc: m.actualBoundingBoxDescent, left: m.actualBoundingBoxLeft, right: m.actualBoundingBoxRight, adv: m.width };
  }

  function relayout(): void {
    if (disposed) return;
    for (const g of glyphs) if (g.ev) endEvent(g, true);
    if (rose) endRose();

    root.classList.add('is-measuring');
    root.style.fontSize = '';
    // scale to fit: the word never wraps and never touches the viewport edge
    const natural = root.getBoundingClientRect().width;
    const avail = innerWidth * TUNE.fitFraction;
    let fs = parseFloat(getComputedStyle(root).fontSize);
    if (natural > avail && natural > 0) {
      fs = fs * (avail / natural);
      root.style.fontSize = `${fs.toFixed(2)}px`;
    }
    rootEm = fs;
    const tldEm = parseFloat(getComputedStyle(tldEl).fontSize);
    for (const g of glyphs) {
      g.em = g.row === 'word' ? rootEm : tldEm;
      const r = g.el.getBoundingClientRect();
      g.w = r.width;
      g.h = r.height;
    }
    for (const g of glyphs) g.el.style.width = `${g.w.toFixed(3)}px`;
    root.classList.remove('is-measuring');

    // centres, baselines, ink boxes (now that widths are fixed)
    const baseline: Record<Row, number> = { word: 0, tld: 0 };
    for (const row of ['word', 'tld'] as Row[]) {
      const first = glyphs.find((g) => g.row === row);
      if (first) baseline[row] = probes[row].getBoundingClientRect().top - first.el.getBoundingClientRect().top;
    }
    for (const g of glyphs) {
      const r = g.el.getBoundingClientRect();
      g.cx = r.left + r.width / 2;
      g.cy = r.top + r.height / 2;
      const ink = inkOf(g);
      const origin = (g.w - ink.adv) / 2;
      const padX = g.em * 0.03;
      const padY = g.em * 0.025;
      const left = origin - ink.left - padX;
      const width = ink.left + ink.right + padX * 2;
      const top = baseline[g.row] - ink.asc - padY;
      const height = ink.asc + ink.desc + padY * 2;
      g.rotEl.style.cssText = `left:${left.toFixed(2)}px;top:${top.toFixed(2)}px;width:${Math.max(2, width).toFixed(2)}px;height:${Math.max(2, height).toFixed(2)}px`;
      if (g === roseGlyph) {
        const xh = ink.asc;
        const size = xh * TUNE.rose.sizeXh;
        const cxBox = origin + (ink.right - ink.left) / 2;
        const cyBox = baseline.tld - xh / 2 - g.em * TUNE.rose.riseEm;
        roseSvg.style.cssText = `left:${(cxBox - size / 2).toFixed(2)}px;top:${(cyBox - size / 2).toFixed(2)}px;width:${size.toFixed(2)}px;height:${size.toFixed(2)}px`;
      }
    }
    // force a rewrite of every inline value next frame
    for (const g of glyphs) { g.lastFvs = ''; g.lx = g.ly = g.lr = NaN; g.ls = NaN; }
  }

  // --- slips: the look-alike is set in the other face, whose Latin-ext subset (Recursive ~310 kB,
  // Fraunces ~105 kB) is fetched lazily by the first slip attempt on that row, never at boot. That
  // attempt is skipped, so a slip is never drawn in a fallback face while the subset arrives.
  // ơ and đ are left out of the warm-up text: FontFaceSet.load() fetches every face whose range
  // covers a character, and those two also sit in the (redundant) vietnamese subset's range.
  const SLIP_FACE: Record<Row, string> = { word: "'Recursive Variable'", tld: "'Fraunces Variable'" };
  const VIET = /[ĂăĐđĨĩŨũƠơƯư]/;
  const slipReady: Record<Row, boolean> = { word: false, tld: false };
  const slipWarming: Record<Row, boolean> = { word: false, tld: false };

  function slipText(row: Row): string {
    let s = '';
    for (const g of glyphs) {
      if (g.row !== row) continue;
      for (const alt of SLIPS[g.ch] ?? []) if (!VIET.test(alt) && !s.includes(alt)) s += alt;
    }
    return s;
  }

  /** true when the row's other face can already render its look-alikes; otherwise starts the fetch */
  function warmSlipFace(row: Row): boolean {
    if (slipReady[row]) return true;
    if (slipWarming[row]) return false;
    slipWarming[row] = true;
    const fonts = document.fonts;
    const text = slipText(row);
    if (!fonts || typeof fonts.load !== 'function' || !text) { slipReady[row] = true; return true; }
    fonts.load(`16px ${SLIP_FACE[row]}`, text).then(
      () => { slipReady[row] = true; },
      () => { slipWarming[row] = false; },
    );
    return false;
  }

  // --- events ------------------------------------------------------------------
  function activeEvents(): number {
    let n = 0;
    for (const g of glyphs) if (g.ev) n++;
    return n;
  }

  function pickKind(): EvKind {
    const w = TUNE.events.weights;
    const r = rnd() * (w.cut + w.slip + w.rot);
    return r < w.cut ? 'cut' : r < w.cut + w.slip ? 'slip' : 'rot';
  }

  function startEvent(g: Glyph, kind: EvKind, force = false): boolean {
    if (!ready || g.ev || (g === roseGlyph && rose)) return false;
    if (reduced && !force) return false;
    if (!force && (activeEvents() >= TUNE.events.maxActive || now - lastEventAt < TUNE.events.cooldown)) return false;
    let dur = 0;
    if (kind === 'cut') {
      const [a, b] = TUNE.events.cutMs;
      dur = (a + rnd() * (b - a)) / 1000;
      g.el.classList.add('cut');
      g.lastFvs = '';
    } else if (kind === 'slip') {
      const list = SLIPS[g.ch];
      if (!list || !list.length || !warmSlipFace(g.row)) return false;
      const [a, b] = TUNE.events.slipMs;
      dur = (a + rnd() * (b - a)) / 1000;
      g.text.textContent = list[Math.floor(rnd() * list.length)];
      g.el.classList.add('slip');
      g.lastFvs = '';
    } else {
      dur = TUNE.events.rotMs / 1000;
      g.el.dataset.rot = '3';
    }
    g.ev = { kind, t0: now, dur, flick: -1, orig: g.ch };
    lastEventAt = now;
    return true;
  }

  function endEvent(g: Glyph, abort = false): void {
    const ev = g.ev;
    if (!ev) return;
    g.ev = null;
    g.el.classList.remove('cut', 'slip');
    g.text.textContent = ev.orig;
    delete g.el.dataset.rot;
    if (ev.kind !== 'rot' || abort) g.lastFvs = '';
  }

  function stepEvent(g: Glyph): void {
    const ev = g.ev;
    if (!ev) return;
    const t = now - ev.t0;
    if (ev.kind === 'rot') {
      const third = ev.dur / 3;
      const level = t < third ? '3' : t < 2 * third ? '2' : t < ev.dur ? '1' : '';
      if (!level) endEvent(g);
      else if (g.el.dataset.rot !== level) g.el.dataset.rot = level;
      return;
    }
    if (ev.kind === 'slip') {
      if (t >= ev.dur) endEvent(g);
      return;
    }
    // cut-in: hard return with a two-frame flicker — off, off, on, on, off — no fade
    if (ev.flick < 0) {
      if (t < ev.dur) return;
      ev.flick = 0;
    }
    const on = ev.flick === 2 || ev.flick === 3;
    const has = g.el.classList.contains('cut');
    if (on !== has) { g.el.classList.toggle('cut', on); g.lastFvs = ''; }
    ev.flick++;
    if (ev.flick > 4) endEvent(g);
  }

  function randomEvent(): void {
    const kind = pickKind();
    const candidates = glyphs.filter((g) => !g.ev && (kind !== 'slip' || SLIPS[g.ch]) && !(g === roseGlyph && rose));
    if (!candidates.length) return;
    const g = candidates[Math.floor(rnd() * candidates.length)];
    startEvent(g, kind);
  }

  function scheduleNext(): void {
    const { minGap, maxGap, rareFactor } = TUNE.events;
    nextEventAt = now + (minGap + rnd() * (maxGap - minGap)) * (rare ? rareFactor : 1);
  }

  function startRose(): void {
    if (!roseGlyph || rose || !ready) return;
    if (roseGlyph.ev) endEvent(roseGlyph, true);
    rose = { t0: now, length: roseGeom.length };
    rosePathEl.style.strokeDashoffset = String(roseGeom.length);
    roseGlyph.el.classList.add('rose');
  }

  function endRose(): void {
    if (!rose || !roseGlyph) return;
    rose = null;
    roseGlyph.el.classList.remove('rose');
    rosePathEl.style.strokeDashoffset = String(roseGeom.length);
  }

  function stepRose(): void {
    if (!rose) return;
    const t = now - rose.t0;
    const { drawSec, holdUntil, totalSec } = TUNE.rose;
    let offset: number;
    if (t < drawSec) offset = rose.length * (1 - easeOutCubic(t / drawSec));
    else if (t < holdUntil) offset = 0;
    else if (t < totalSec) offset = rose.length * easeInCubic((t - holdUntil) / (totalSec - holdUntil));
    else { endRose(); return; }
    rosePathEl.style.strokeDashoffset = offset.toFixed(2);
  }

  // --- bus -----------------------------------------------------------------------
  const speed = CONFIG.wave.speedPxPerSec;

  function onStrike(e: BusEvents['strike']): void {
    const v = clamp01(e.velocity);
    heat = Math.min(1, heat + v * TUNE.strike.heatGain);
    const { gamma, floor, falloffPx } = TUNE.strike;
    const strength = floor + (1 - floor) * Math.pow(v, gamma);
    for (const g of glyphs) {
      const d = Math.hypot(g.cx - e.screen.x, g.cy - e.screen.y);
      const q = d / falloffPx;
      const mag = strength / (1 + q * q);
      if (mag < 0.02) continue;
      g.kicks.push({ at: now + d / speed, mag });
    }
  }

  function onChord(): void { startRose(); }

  const offStrike = bus.on('strike', onStrike);
  const offChord = bus.on('chord', onChord);

  // --- debug --------------------------------------------------------------------
  let nextSynthStrike = 0;
  let nextSynthChord = 0;
  let lastPointer: PointerInfo | null = null;

  function synthStrike(x: number, y: number, velocity: number): void {
    bus.emit('strike', {
      type: 'strike', tube: Math.floor(rnd() * 8), velocity, kind: 'clapper',
      pos: { x: 0, y: 1, z: 0 }, s: 0.75, screen: { x, y },
    });
  }

  function onKey(e: KeyboardEvent): void {
    if (e.altKey || e.ctrlKey || e.metaKey) return;
    const idle = (row?: Row) => glyphs.filter((g) => !g.ev && (!row || g.row === row) && !(g === roseGlyph && rose));
    const pick = (list: Glyph[]) => list[Math.floor(rnd() * list.length)];
    switch (e.key) {
      case '1': { const c = idle(); if (c.length) startEvent(pick(c), 'cut', true); break; }
      case '2': { const c = idle().filter((g) => SLIPS[g.ch]); if (c.length) startEvent(pick(c), 'slip', true); break; }
      case '3': { const c = idle(); if (c.length) startEvent(pick(c), 'rot', true); break; }
      case '4': case 'r': case 'R': startRose(); break;
      case '5': {
        const x = lastPointer?.active ? lastPointer.x : rnd() * innerWidth;
        const y = lastPointer?.active ? lastPointer.y : rnd() * innerHeight;
        synthStrike(x, y, 0.85);
        break;
      }
      case 'h': case 'H': console.info(DEBUG_HELP[0], 'font-weight:700', '', '\n' + DEBUG_HELP[1]); break;
      default: return;
    }
  }

  if (debug) {
    window.addEventListener('keydown', onKey);
    console.info(DEBUG_HELP[0], 'font-weight:700', '', '\n' + DEBUG_HELP[1]);
  }

  // --- per-frame ----------------------------------------------------------------
  const [kW, cW] = K.wght;
  const [kS, cS] = K.soft;
  const [kP, cP] = K.pos;
  const [kR, cR] = K.rot;
  const [kC, cC] = K.scale;

  function update(dt: number, t: number, pointer: PointerInfo | null): void {
    if (disposed) return;
    now = t;
    lastPointer = pointer;
    heat *= Math.exp(-dt / TUNE.strike.heatTau);

    if (debug && ready) {
      if (now >= nextSynthStrike) {
        synthStrike(rnd() * innerWidth, rnd() * innerHeight, 0.3 + rnd() * 0.7);
        nextSynthStrike = now + 1.5 + rnd() * 2.5;
      }
      if (now >= nextSynthChord) {
        bus.emit('chord', { tubes: [0, 2, 4, 6], strength: 0.5 });
        nextSynthChord = now + 20;
      }
    }

    // scheduler: heat runs the clock faster
    if (ready) {
      nextEventAt -= dt * TUNE.events.heatAccel * heat;
      if (now >= nextEventAt) { randomEvent(); scheduleNext(); }
    }

    // pointer velocity in px/s (PointerInfo carries a smoothed one; use it, fall back to finite difference)
    let pvx = 0;
    let pvy = 0;
    if (pointer && pointer.active) {
      pvx = pointer.vx; pvy = pointer.vy;
      if (pointerPrev && pvx === 0 && pvy === 0 && dt > 0) {
        pvx = (pointer.x - pointerPrev.x) / dt;
        pvy = (pointer.y - pointerPrev.y) / dt;
      }
      if (!pointerPrev) pointerPrev = { x: pointer.x, y: pointer.y };
      else { pointerPrev.x = pointer.x; pointerPrev.y = pointer.y; }
    } else {
      pointerPrev = null;
    }
    const pspeed = Math.min(TUNE.pointer.maxSpeed, Math.hypot(pvx, pvy));

    const n = Math.max(1, Math.ceil(dt / TUNE.maxStep));
    const h = dt / n;

    for (const g of glyphs) {
      // --- discrete arrivals of the wave front
      if (g.kicks.length) {
        let w = 0;
        for (let i = 0; i < g.kicks.length; i++) {
          const kick = g.kicks[i];
          if (kick.at > now) { g.kicks[w++] = kick; continue; }
          if (now - kick.at > 1) continue; // stale (tab was asleep)
          const m = kick.mag * motion;
          g.wght.v += m * TUNE.strike.wghtKick * (0.85 + rnd() * 0.3);
          g.soft.v += m * TUNE.strike.softKick;
          g.y.v += m * TUNE.strike.yKickEm;
          g.rot.v += m * TUNE.strike.rotKickDeg * (rnd() < 0.5 ? -1 : 1);
          if (kick.mag > TUNE.events.waveMinMag && rnd() < TUNE.events.waveTrigger * kick.mag) startEvent(g, pickKind());
        }
        g.kicks.length = w;
      }

      // --- drifting rest
      const tt = now / g.period + g.phase;
      const isWord = g.row === 'word';
      const wRest = TUNE.rest.wght + TUNE.rest.wghtDrift * motion * noise.noise2(g.seedOff, tt);
      const sRest = isWord
        ? TUNE.rest.soft + TUNE.rest.softDrift * motion * noise.noise2(g.seedOff + 3.1, tt * 1.27)
        : (TUNE.rest.casl + TUNE.rest.caslDrift * motion * noise.noise2(g.seedOff + 3.1, tt * 1.27)) * 100;

      // --- pointer: lean away, take the brush
      let tx = 0;
      let ty = 0;
      let tr = 0;
      let ax = 0;
      let ay = 0;
      let ar = 0;
      let tScale = 1;
      if (pointer && pointer.active) {
        const dx = g.cx - pointer.x;
        const dy = g.cy - pointer.y;
        const d = Math.hypot(dx, dy) || 1e-3;
        const R = TUNE.pointer.radiusPx;
        if (d < R) {
          const f = (1 - d / R) * (1 - d / R);
          const ux = dx / d;
          const uy = dy / d;
          tx = ux * f * TUNE.pointer.leanEm * motion;
          ty = uy * f * TUNE.pointer.leanYEm * motion;
          tr = ux * f * TUNE.pointer.leanDeg * motion;
          if (pspeed > 30) {
            const s = pspeed / TUNE.pointer.maxSpeed;
            ax = (pvx / g.em) * f * TUNE.pointer.gain * motion * s;
            ay = (pvy / g.em) * f * TUNE.pointer.gain * motion * s;
            ar = (pvx / g.em) * f * TUNE.pointer.rotGain * motion * s;
          }
        }
        if (g === dot) {
          const on = g.apple ? d < TUNE.pointer.appleRadiusPx * 1.3 : d < TUNE.pointer.appleRadiusPx;
          if (on !== g.apple) { g.apple = on; g.el.classList.toggle('apple', on); }
          if (on) tScale = TUNE.pointer.appleScale;
        }
      } else if (g.apple) {
        g.apple = false;
        g.el.classList.remove('apple');
      }

      // --- integrate
      for (let i = 0; i < n; i++) {
        springTo(g.wght, wRest, kW, cW, h);
        springTo(g.soft, sRest, kS, cS, h);
        g.x.v += ax * h; g.y.v += ay * h; g.rot.v += ar * h;
        springTo(g.x, tx, kP, cP, h);
        springTo(g.y, ty, kP, cP, h);
        springTo(g.rot, tr, kR, cR, h);
        springTo(g.scale, tScale, kC, cC, h);
      }
      const L = TUNE.limit;
      g.wght.x = clamp(g.wght.x, L.wght[0], L.wght[1]);
      g.soft.x = clamp(g.soft.x, L.soft[0], L.soft[1]);
      g.x.x = clamp(g.x.x, -L.offsetEm, L.offsetEm);
      g.y.x = clamp(g.y.x, -L.offsetEm, L.offsetEm);
      g.rot.x = clamp(g.rot.x, -L.rotDeg, L.rotDeg);
      g.scale.x = clamp(g.scale.x, L.scale[0], L.scale[1]);

      // --- events
      if (g.ev) stepEvent(g);

      // --- write: font-variation-settings (only on a real change)
      const cutOn = g.ev?.kind === 'cut' && g.el.classList.contains('cut');
      const slipOn = g.ev?.kind === 'slip';
      const other = cutOn || slipOn; // set in the other face right now (.cut / .slip in logo.css)
      const mono = isWord ? other : !other;
      const wght = Math.round(mono && isWord ? g.wght.x + TUNE.otherFace.wghtComp : g.wght.x);
      let fvs: string;
      if (mono) {
        const casl = isWord ? (slipOn ? TUNE.slip.casl : 1) : clamp(g.soft.x / 100, L.casl[0], L.casl[1]);
        fvs = `'MONO' 1, 'CASL' ${casl.toFixed(2)}, 'wght' ${wght}, 'slnt' 0, 'CRSV' 0`;
      } else {
        const soft = clamp(isWord ? g.soft.x : 45 + g.soft.x, 0, 100);
        fvs = `'opsz' 144, 'wght' ${wght}, 'SOFT' ${soft.toFixed(1)}, 'WONK' 1, 'MONO' 1, 'CASL' 0`;
      }
      if (fvs !== g.lastFvs) { g.el.style.fontVariationSettings = fvs; g.lastFvs = fvs; }

      // --- write: transform
      const px = g.x.x * g.em;
      const py = g.y.x * g.em;
      const rot = g.rot.x;
      const sc = g.scale.x;
      if (Math.abs(px - g.lx) > 0.12 || Math.abs(py - g.ly) > 0.12 || Math.abs(rot - g.lr) > 0.04 || Math.abs(sc - g.ls) > 0.003 || Number.isNaN(g.lx)) {
        const still = Math.abs(px) < 0.05 && Math.abs(py) < 0.05 && Math.abs(rot) < 0.02 && Math.abs(sc - 1) < 0.002;
        g.el.style.transform = still ? 'none' : `translate(${px.toFixed(2)}px, ${py.toFixed(2)}px) rotate(${rot.toFixed(2)}deg) scale(${sc.toFixed(3)})`;
        g.lx = still ? 0 : px; g.ly = still ? 0 : py; g.lr = still ? 0 : rot; g.ls = still ? 1 : sc;
      }
    }

    if (rose) stepRose();
  }

  // --- boot -------------------------------------------------------------------------
  relayout();
  void (async () => {
    try { await document.fonts.ready; } catch { /* no Font Loading API */ }
    if (disposed) return;
    relayout();
    ready = true;
    scheduleNext();
    nextSynthStrike = now + 1;
    nextSynthChord = now + 6;
  })();

  function dispose(): void {
    if (disposed) return;
    disposed = true;
    offStrike();
    offChord();
    if (debug) window.removeEventListener('keydown', onKey);
    root.remove();
  }

  const handle: LogoHandle = { update, relayout, dispose };
  if (DEV) {
    handle.dev = {
      event: (kind, index) => { const g = glyphs[index]; return g ? startEvent(g, kind, true) : false; },
      rose: startRose,
      slipReady: () => ({ ...slipReady }),
    };
  }
  return handle;
}
